import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar, Box, CssBaseline, Divider, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemText, Toolbar, Typography, Select, MenuItem,
  Tooltip, Button, useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';  // ✅ ADDED MenuIcon
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import TranslateIcon from '@mui/icons-material/Translate';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import style from './Navbar.module.css';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import { userProfile } from '../../features/auth/authAction';
import getUserToken from '../../helpers/tokenValidation';
import { persistor } from '../../app/store';

const drawerWidth = 220;

const Navbar = ({ toggleTheme, currentTheme, language, setLanguage, ...props }) => {
  // ✅ Use MUI theme hook to access dynamic theme
  const theme = useTheme();

  const t = {
    si: {
      brand: 'යුවතිපතී',
      groom: 'මනාලයන්',
      bride: 'මනාලියන්',
      pricing: 'පැකේජ',
      postAd: 'දැන්වීමක් පළ කරන්න',
      yourAccount: 'ඔබගේ ගිණුම',
      login: 'ඔබේ ගිණුමට ඇතුල් වෙන්න',
      signup: 'ලියාපදිංචි කරන්න',
      logout: 'පිටවීම',
      about: 'අප ගැන',
    },
    en: {
      brand: 'Yuwathipathi',
      groom: 'Grooms',
      bride: 'Brides',
      pricing: 'Pricing',
      postAd: 'Post an Ad',
      yourAccount: 'Your account',
      login: 'Log in',
      signup: 'Sign up',
      logout: 'Log out',
      about: 'About Us',
    }
  }[language || 'si'];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.auth?.success);
  const loggedUserDetails = useSelector((state) => state.auth?.profileInfo?.user?.id);
  const userSpecificDetails = useSelector(state => state.user?.userSpecificData);
  const permission = useSelector(state => state.auth?.permission?.user_permission);

  const [mobileOpen, setMobileOpen] = useState(false);
  const reduxToken = useSelector((state) => state.auth?.userToken);
  // Reactive authorization check: Redux state OR valid token in localStorage
  const authorize = !!(reduxToken || getUserToken());

  useEffect(() => {
    if (authorize && !userDetails) {
      dispatch(userProfile());
    }
  }, [authorize, userDetails, dispatch]);

  const handleLogout = async () => {
    dispatch(logout());
    // Purge the persisted state before reload to avoid rehydration of old state
    await persistor.purge();
    navigate('/', { replace: true });
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  // Mobile drawer content
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <NavLink to="/" style={{ color: 'black', textDecoration: 'none' }}>
        <Typography variant="h6" sx={{ my: 2 }}>
          {t.brand}
        </Typography>
      </NavLink>
      <Divider />
      <List>
        {[
          { text: t.groom, link: '/service-groom' },
          { text: t.bride, link: '/service-bride' },
          { text: t.pricing, link: '/pricing-page' },
          { text: t.about, link: '/infor-page' },
          // {
          //   text: userSpecificDetails ? t.yourAccount : t.postAd,
          //   link: userSpecificDetails
          //     ? `/moreDetailsPage/${loggedUserDetails}/${loggedUserDetails}`
          //     : `/service/${loggedUserDetails}`
          // },
          {
            text: userSpecificDetails ? t.yourAccount : t.postAd,
            link: userSpecificDetails
              ? `/profile`
              : `/service/${loggedUserDetails}`
          },
          { text: !authorize ? t.login : t.logout, link: !authorize ? '/Login' : '/', onClick: authorize ? handleLogout : null },
          { text: !authorize ? t.signup : '', link: !authorize ? '/SignUp' : '' },
        ].map((item, index) => (
          item.text && (
            <ListItem key={index} disablePadding>
              <ListItemButton sx={{ textAlign: 'center' }}>
                <ListItemText>
                  <NavLink style={{ color: 'inherit', textDecoration: 'none' }} to={item.link} onClick={item.onClick}>
                    {item.text}
                  </NavLink>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          )
        ))}

        {/* Theme and Language Toggles for mobile drawer */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexDirection: 'column' }}>
          <Tooltip title={currentTheme === 'modern' ? 'Switch to Classic Layout' : 'Switch to Modern Layout'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                mb: 2,
                color: 'black',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <DashboardIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title={language === 'si' ? 'Switch to English' : 'සිංහලට මාරු වන්න'}>
              <IconButton
                onClick={() => setLanguage(language === 'si' ? 'en' : 'si')}
                sx={{
                  color: 'black',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: '40px',
                }}
                size="small"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TranslateIcon sx={{ fontSize: '1.1rem' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    {language === 'si' ? 'EN' : 'සිං'}
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </List >
    </Box >
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="sticky"
        component="nav"
        sx={{
          p: 1,
          // backgroundColor: theme.palette.custom?.navbarBg || theme.palette.primary.main,
          backgroundColor: theme.palette.custom?.navbarBg || theme.palette.primary.main,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          color: theme.palette.custom?.navbarText || 'white',
          transition: 'all 0.3s ease'
        }}
      >
        <Toolbar>

          <NavLink to="/" style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
            <Typography
              variant="h5"
              component="div"
              className={style.homeButton}
              sx={{
                display: { xs: 'block', sm: 'block', md: 'block' },
                color: theme.palette.custom?.logoColor || 'inherit',
                fontFamily: "'Playfair Display', 'Abhaya Libre', serif",
                fontWeight: 700,
                fontSize: '2.4rem',
                letterSpacing: '-0.02em',
                transition: 'color 0.3s ease'
              }}
            >
              {t.brand}
            </Typography>
          </NavLink>
          <IconButton
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{ ml: 2, display: { sm: 'block', lg: 'none' }, color: 'inherit' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Nav Links Section */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
            <NavLink className={style.navItemDesign} to='/service-groom'>{t.groom}</NavLink>
            <NavLink className={style.navItemDesign} to='/service-bride'>{t.bride}</NavLink>
            <NavLink className={style.navItemDesign} to='/pricing-page'>{t.pricing}</NavLink>
            <NavLink className={style.navItemDesign} to='/infor-page'>{t.about}</NavLink>

            {
              !userSpecificDetails ? (
                <NavLink
                  className={style.navItemDesign}
                  to={`./service/${loggedUserDetails}`}
                >
                  {t.postAd}
                </NavLink>
              ) : userSpecificDetails ? (
                // <NavLink
                //   className={style.navItemDesign}
                //   to={`./moreDetailsPage/${loggedUserDetails}/${loggedUserDetails}`}
                // >
                //   {t.yourAccount}
                // </NavLink>
                <NavLink
                  className={style.navItemDesign}
                  to={'/profile'}
                >
                  {t.yourAccount}
                </NavLink>
              ) : (
                <NavLink
                  className={style.navItemDesign}
                  to='/login'
                >
                  දැන්වීමක් පළ කරන්න
                </NavLink>
              )
            }

            {!authorize && <NavLink className={style.navItemDesign} to='/Login'>{t.login}</NavLink>}
            {!authorize ? (
              <NavLink className={style.navItemDesignSignUp} to='/SignUp'>{t.signup}</NavLink>
            ) : (
              <NavLink className={style.conditionalClassName} to='/' onClick={handleLogout}>{t.logout}</NavLink>
            )}

            <Tooltip title={currentTheme === 'modern' ? 'Classic Layout' : 'Modern Layout'}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  ml: 2,
                  color: 'inherit',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <DashboardIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Language Toggle - Right Side (Desktop) */}
          <Box sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: 1,
            ml: 1,
            mr: 2,
          }}>
            <Tooltip title={language === 'si' ? 'Switch to English' : 'සිංහලට මාරු වන්න'}>
              <IconButton
                onClick={() => setLanguage(language === 'si' ? 'en' : 'si')}
                sx={{
                  color: 'inherit',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                  minWidth: '40px',
                }}
                size="small"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TranslateIcon sx={{ fontSize: '1.1rem' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    {language === 'si' ? 'EN' : 'සිං'}
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Toolbar id="back-to-top-anchor" />
      <nav>
        <Drawer
          anchor="right"
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
};

Navbar.propTypes = {
  window: PropTypes.func,
};

export default Navbar;
